import * as React from 'react';

import {BoxRegularIcon} from 'shells/icon/svg/box-regular-icon';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CompetitorRegularIcon} from 'shells/icon/svg/competitor-regular-icon';
import {EnvelopesRegularIcon} from 'shells/icon/svg/envelopes-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlAttachmentIcon} from 'shells/icon/svg/html-attachment-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PeepsBookIcon} from 'shells/icon/svg/peeps-book';
// @ts-expect-error - imported from .js/.jsx file
import {PencilToPaperIcon} from 'shells/icon/svg/pencil-to-paper-icon';
import {QuoteRegularIcon} from 'shells/icon/svg/quote-regular-icon';
import {SourcesIcon} from 'shells/icon/svg/sources-icon';
import {TaskTickOutlineIcon} from 'shells/icon/svg/task-tick-outline';

import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {useUpdateWidgetCollapsed} from './graphql/hooks';
import type {CardWidgetKey, CardWidgetSettingName} from './types';

const widgetKeyToSettingNameMap = {
    leadSalesDocuments: 'widget_lead_sales_documents_collapsed',
    leadCompaniesAndPeople: 'widget_lead_companies_and_people_collapsed',
    leadProducts: 'widget_lead_products_collapsed',
    leadChannelsAndSources: 'widget_lead_channels_and_sources_collapsed',
    leadPipeline: 'widget_lead_pipeline_collapsed',
    leadTopCustomFields: 'widget_lead_top_custom_fields_collapsed',
    leadCustomFields: 'widget_lead_custom_fields_collapsed',
    leadCompetitors: 'widget_lead_competitors_collapsed',
    leadAttachments: 'widget_lead_attachments_collapsed',
    leadScheduledActivities: 'widget_lead_scheduled_activities_collapsed',
    leadLoggedActivities: 'widget_lead_logged_activities_collapsed',
    leadUpcomingTasks: 'widget_lead_upcoming_tasks_collapsed',
    leadFutureTasks: 'widget_lead_future_tasks_collapsed',
    leadCompletedTasks: 'widget_lead_completed_tasks_collapsed',
    leadDripSequences: 'widget_lead_drip_sequences_collapsed',
    leadPersonalEmailSequences: 'widget_lead_personal_email_sequences_collapsed',
} as const;

const widgetKeyToTitleMap = {
    leadSalesDocuments: 'QUOTES & INVOICES',
    leadCompaniesAndPeople: 'COMPANIES & PEOPLE',
    leadProducts: 'PRODUCTS',
    leadChannelsAndSources: 'SOURCES',
    leadPipeline: 'PIPELINE',
    leadTopCustomFields: 'TOP CUSTOM FIELDS',
    leadCustomFields: 'STANDARD CUSTOM FIELDS',
    leadCompetitors: 'COMPETITORS',
    leadAttachments: 'ATTACHMENTS',
    leadScheduledActivities: 'SCHEDULED ACTIVITIES',
    leadLoggedActivities: 'LOGGED ACTIVITIES',
    leadUpcomingTasks: 'UPCOMING TASKS',
    leadFutureTasks: 'FUTURE TASKS',
    leadCompletedTasks: 'COMPLETED TASKS',
    leadDripSequences: 'DRIP SEQUENCES',
    leadPersonalEmailSequences: 'PERSONAL EMAIL SEQUENCES',
} as const;

const widgetKeyToEmptyStateMap = {
    leadSalesDocuments: {
        text: 'No quotes or invoices added',
        icon: QuoteRegularIcon,
        iconSize: 22,
    },
    leadCompaniesAndPeople: {
        text: 'No companies or people added',
        icon: PeepsBookIcon,
        iconSize: 22,
    },
    leadProducts: {
        text: 'No products added',
        icon: BoxRegularIcon,
        iconSize: 22,
    },
    leadChannelsAndSources: {
        text: 'No sources added',
        icon: SourcesIcon,
        iconSize: 22,
    },
    leadPipeline: {
        text: 'No pipeline stages added',
        icon: CalendarSquareRegularActivityIcon,
        iconSize: 22,
    },
    leadTopCustomFields: {
        text: 'No custom fields added',
        icon: PencilToPaperIcon,
        iconSize: 22,
    },
    leadCustomFields: {
        text: 'No custom fields added',
        icon: PencilToPaperIcon,
        iconSize: 22,
    },
    leadCompetitors: {
        text: 'No competitors added',
        icon: CompetitorRegularIcon,
        iconSize: 22,
    },
    leadAttachments: {
        text: 'No attachments uploaded',
        icon: HtmlAttachmentIcon,
        iconSize: 22,
    },
    leadScheduledActivities: {
        text: 'No scheduled activities',
        icon: CalendarSquareRegularActivityIcon,
        iconSize: 22,
    },
    leadLoggedActivities: {
        text: 'No logged activities',
        icon: CalendarSquareRegularActivityIcon,
        iconSize: 22,
    },
    leadUpcomingTasks: {
        text: 'No upcoming tasks',
        icon: TaskTickOutlineIcon,
        iconSize: 18,
    },
    leadFutureTasks: {
        text: 'No future tasks',
        icon: TaskTickOutlineIcon,
        iconSize: 18,
    },
    leadCompletedTasks: {
        text: 'No completed tasks',
        icon: TaskTickOutlineIcon,
        iconSize: 18,
    },
    leadDripSequences: {
        text: 'No active drip sequences',
        icon: EnvelopesRegularIcon,
        iconSize: 20,
    },
    leadPersonalEmailSequences: {
        text: 'No personal email sequences',
        icon: EnvelopesRegularIcon,
        iconSize: 20,
    },
} as const;

export function getWidgetSettingName(widgetKey: CardWidgetKey): CardWidgetSettingName {
    return widgetKeyToSettingNameMap[widgetKey];
}

export function getWidgetTitle(widgetKey: CardWidgetKey, count?: number | null): string {
    if (count != null) {
        return `${widgetKeyToTitleMap[widgetKey]} (${count})`;
    }

    return widgetKeyToTitleMap[widgetKey];
}

export function getWidgetEmptyStateProps(widgetKey: CardWidgetKey, isEmpty: boolean) {
    const emptyStateConfig = widgetKeyToEmptyStateMap[widgetKey];

    if (!emptyStateConfig) {
        return undefined;
    }

    return {
        isEmpty,
        ...emptyStateConfig,
    };
}

export function useCollapsible(
    widgetKey: CardWidgetKey,
    user?: User | null
): {
    isCollapsed: boolean;
    onChange: () => void;
} {
    const updateWidgetCollapsedSetting = useUpdateWidgetCollapsed();

    const isCollapsed = React.useMemo(() => getIsCollapsed(widgetKey, user), [widgetKey, user]);

    const onChange = React.useCallback(() => {
        if (!user || !widgetKey) {
            return;
        }

        updateWidgetCollapsedSetting({
            userId: user.id,
            widgetKey,
            value: isCollapsed ? '0' : '1',
        });
    }, [widgetKey, user, updateWidgetCollapsedSetting, isCollapsed]);

    return React.useMemo(
        () => ({
            isCollapsed,
            onChange,
        }),
        [isCollapsed, onChange]
    );
}

export function getIsCollapsed(widgetKey: CardWidgetKey, user?: User | null): boolean {
    if (!user) {
        return true;
    }

    return user.widgetsCollapsedState[widgetKey];
}
